import {
  CallParticipant,
  CallParticipantPermitsUpdate,
  CallParticipantState,
  CallParticipantStatesUpdate,
  CallParticipantStatusUpdate,
  CallTypeApi,
  CallUserType,
  ParticipantStatus,
  SetCallParticipantPermitsPayload,
} from '@nauTypes/callType';
import {
  END_CALL,
  END_CALL_FAILED,
  END_CALL_SUCCESS,
  GET_CALL_DATA,
  GET_CALL_DATA_FAILED,
  GET_CALL_DATA_SUCCESS,
  GET_PARTICIPANTS,
  GET_PARTICIPANTS_FAILED,
  GET_PARTICIPANTS_SUCCESS,
  SET_PARTICIPANTS_PERMITS,
  SET_PARTICIPANTS_PERMITS_FAILED,
  SET_PARTICIPANTS_PERMITS_SUCCESS,
  SET_PARTICIPANT_STATE,
  SET_PARTICIPANT_STATUS,
  UPDATE_PARTICIPANTS_PERMITS,
  UPDATE_PARTICIPANT_STATE,
  UPDATE_PARTICIPANT_STATUS,
} from 'redux/constants/calls';
import { RequireAtLeastOne } from 'shared/types';
import { ChatDescription } from 'types/chatsType';
import { ActionType, PayloadErrorType } from 'types/reduxTypes';
import {
  CALL_ACTIVATED,
  CALL_HANDLE_INCOMING,
  CALL_HANDLE_INCOMING_FAILED,
  CALL_HANDLE_INCOMING_SUCCESS,
  CALL_MINIFY,
  CALL_RECEIVE,
  CALL_REDIAL,
  CALL_REQUEST_JOIN,
  CALL_START,
  CALL_TERMINATE,
} from '../constants';

export type CallPayloadType = {
  dialogId: number | null;
  caller: CallUserType | null;
  callerId?: string;
  conference: boolean;
  participants?: CallParticipant[];
};

export type IncomingCallType = {
  cType: number;
  chats: ChatDescription[];
} & Pick<CallPayloadType, 'dialogId' | 'callerId'>;

export type JoinUserType = Pick<CallPayloadType, 'callerId'>;
export type RedialType = Pick<CallUserType, 'userId'> & Pick<CallPayloadType, 'dialogId'>;

export type GetParticipantsPayload = {
  dialog: ChatDescription['id'];
  details?: number;
};

export type GetCallDataProps = {
  dialog: ChatDescription['id'];
  details?: number;
};

export type GetCallDataPayload = Pick<CallTypeApi, 'id' | 'startDate' | 'duration'>;

export type SetParticipantStatusPayload = {
  dialog: ChatDescription['id'];
  user: CallParticipant['user'];
  status: ParticipantStatus;
};

type SetParticipantStatePayload = {
  user: string;
  streamName?: string;
  state: RequireAtLeastOne<CallParticipantState>;
};

export type SetParticipantStateProps = {
  dialogId: number;
  statePayload: SetParticipantStatePayload;
};

export function startCall(payload: CallPayloadType): ActionType<CallPayloadType> {
  return {
    payload,
    type: CALL_START,
  };
}
export function receiveCall(payload: CallPayloadType): ActionType<CallPayloadType> {
  return {
    payload,
    type: CALL_RECEIVE,
  };
}
export function handleIncomingCall(payload: IncomingCallType): ActionType<IncomingCallType> {
  return {
    payload,
    type: CALL_HANDLE_INCOMING,
  };
}
export function handleIncomingCallSuccess(payload: CallPayloadType): ActionType<CallPayloadType> {
  return {
    payload,
    type: CALL_HANDLE_INCOMING_SUCCESS,
  };
}
export function handleIncomingCallFailed(payload: unknown): ActionType<unknown> {
  return {
    payload,
    type: CALL_HANDLE_INCOMING_FAILED,
  };
}
export function handleCallActivated(payload: unknown): ActionType<unknown> {
  return {
    payload,
    type: CALL_ACTIVATED,
  };
}
export function requestJoinCall(payload: CallPayloadType): ActionType<CallPayloadType> {
  return {
    payload,
    type: CALL_REQUEST_JOIN,
  };
}
export function terminateCall(): ActionType<unknown> {
  return {
    payload: null,
    type: CALL_TERMINATE,
  };
}
export function endCall(payload: ChatDescription['id']): ActionType<typeof payload> {
  return {
    payload,
    type: END_CALL,
  };
}
export function endCallSuccess(): ActionType<unknown> {
  return {
    payload: null,
    type: END_CALL_SUCCESS,
  };
}
export function endCallFailed(payload: PayloadErrorType): ActionType<typeof payload> {
  return {
    payload,
    type: END_CALL_FAILED,
  };
}
export function redial(payload: RedialType): ActionType<unknown> {
  return {
    payload,
    type: CALL_REDIAL,
  };
}
export function minifyCall(payload: boolean): ActionType<boolean> {
  return {
    payload,
    type: CALL_MINIFY,
  };
}
export function getCallData(payload: GetCallDataProps): ActionType<typeof payload> {
  return {
    payload,
    type: GET_CALL_DATA,
  };
}
export function getCallDataFailed(payload: PayloadErrorType): ActionType<PayloadErrorType> {
  return {
    payload,
    type: GET_CALL_DATA_FAILED,
  };
}
export function getCallDataSuccess(payload: GetCallDataPayload): ActionType<typeof payload> {
  return {
    payload,
    type: GET_CALL_DATA_SUCCESS,
  };
}
export function getParticipants(payload: GetParticipantsPayload): ActionType<typeof payload> {
  return {
    payload,
    type: GET_PARTICIPANTS,
  };
}
export function getParticipantsFailed(payload: PayloadErrorType): ActionType<typeof payload> {
  return {
    payload,
    type: GET_PARTICIPANTS_FAILED,
  };
}
export function getParticipantsSuccess(payload: CallParticipant[]): ActionType<typeof payload> {
  return {
    payload,
    type: GET_PARTICIPANTS_SUCCESS,
  };
}
export function updateParticipantStatus(payload: CallParticipantStatusUpdate): ActionType<typeof payload> {
  return {
    payload,
    type: UPDATE_PARTICIPANT_STATUS,
  };
}
export function updateParticipantsPermits(payload: CallParticipantPermitsUpdate): ActionType<typeof payload> {
  return {
    payload,
    type: UPDATE_PARTICIPANTS_PERMITS,
  };
}
export function setParticipantsPermits(payload: SetCallParticipantPermitsPayload): ActionType<typeof payload> {
  return {
    payload,
    type: SET_PARTICIPANTS_PERMITS,
  };
}
export function setParticipantsPermitsFailed(payload: unknown): ActionType<typeof payload> {
  return {
    payload,
    type: SET_PARTICIPANTS_PERMITS_FAILED,
  };
}
export function setParticipantsPermitsSuccess(payload: unknown): ActionType<typeof payload> {
  return {
    payload,
    type: SET_PARTICIPANTS_PERMITS_SUCCESS,
  };
}
export function setParticipantStatus(payload: SetParticipantStatusPayload): ActionType<typeof payload> {
  return {
    payload,
    type: SET_PARTICIPANT_STATUS,
  };
}
export function setParticipantState(payload: SetParticipantStateProps): ActionType<typeof payload> {
  return {
    payload,
    type: SET_PARTICIPANT_STATE,
  };
}
export function updateParticipantState(payload: CallParticipantStatesUpdate): ActionType<typeof payload> {
  return {
    payload,
    type: UPDATE_PARTICIPANT_STATE,
  };
}
